import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Plus, Zap, Sparkles, ArrowRight, Clock } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { payWithRazorpay } from "@/lib/razorpay";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtINR(paise) {
  if (paise == null) return "—";
  const rupees = Math.round(paise / 100);
  return "₹" + rupees.toLocaleString("en-IN");
}

export default function Billing() {
  const { sub, refresh, loading } = useSubscription();
  const [history, setHistory] = useState({ subscription_payments: [], credit_purchases: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/subscription/history").then((r) => setHistory(r.data)).catch(() => {});
  }, [sub]);

  const doSubscribe = async () => {
    setBusy(true);
    try {
      const result = await payWithRazorpay({ kind: "subscription" });
      if (result?.verified) {
        toast.success("Standard plan activated");
        await refresh();
      }
    } catch (e) {
      const msg = e?.response ? formatApiError(e) : (e?.message || "Payment cancelled");
      if (msg !== "Payment cancelled") toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const doBuy = async (videos) => {
    setBusy(true);
    try {
      const result = await payWithRazorpay({ kind: "credits", videos });
      if (result?.verified) {
        toast.success(`+${videos} credits added`);
        await refresh();
      }
    } catch (e) {
      const msg = e?.response ? formatApiError(e) : (e?.message || "Payment cancelled");
      if (msg !== "Payment cancelled") toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const plan = sub?.plan;
  const packs = sub?.credit_packs || [];
  const maxExtra = sub?.max_extra_per_cycle || 50;
  const q = sub?.quota;
  const alreadyPurchased = q?.extra_purchased || 0;
  const canPurchaseMax = maxExtra - alreadyPurchased;

  const includedPct = q ? Math.min(100, (q.included_used / q.included_total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F0F9FF]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">/ Billing & Usage</p>
        <h1 className="mt-2 font-heading font-black text-4xl sm:text-5xl tracking-tighter">
          Your plan, your credits.
        </h1>

        {loading && <p className="mt-8 font-mono text-xs text-zinc-500">Loading…</p>}

        {!loading && sub && !sub.subscribed && (
          <section data-testid="subscribe-cta" className="mt-10 border-2 border-black bg-white">
            <div className="p-6 sm:p-8 border-b-2 border-black bg-[#DBEAFE]">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#1E40AF]">/ Standard plan</p>
                {plan?.discount_percent > 0 && (
                  <span
                    data-testid="discount-badge"
                    className="font-mono text-[10px] uppercase tracking-widest bg-[#FF3333] text-white px-2 py-0.5 border border-black"
                  >
                    Launch offer · {plan.discount_percent}% off
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                <h2 data-testid="plan-price" className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">
                  {plan?.price_display || "₹699"}
                </h2>
                {plan?.mrp_display && plan.mrp_display !== plan.price_display && (
                  <span data-testid="plan-mrp" className="font-heading font-bold text-2xl text-zinc-400 line-through decoration-2">
                    {plan.mrp_display}
                  </span>
                )}
                <span className="font-mono text-xs text-zinc-600">/ month</span>
              </div>
              <p className="mt-2 text-sm text-zinc-700">
                33 note generations per 30-day cycle. Buy more credits any time — ₹24 each.
              </p>
            </div>
            <ul className="p-6 space-y-2 text-sm">
              {[
                "33 notes / month included",
                "All inputs: text, PDF, URL, YouTube, audio & video (up to ~5 h)",
                "GPT-5.2 lecture & meeting formats",
                "Subject-aware schemas + Mind Maps",
                "DOCX / PDF / Markdown exports",
                "Shareable links + Team Spaces",
                "Add more credits any time (packs of 5–50, max +50 / cycle)",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#3B82F6] mt-0.5 flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="px-6 pb-6">
              <button
                data-testid="btn-subscribe"
                onClick={doSubscribe}
                disabled={busy}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 font-mono text-sm uppercase tracking-widest px-6 py-4 bg-[#3B82F6] text-white btn-brutal disabled:opacity-60"
              >
                {busy ? "Opening checkout…" : `Subscribe · Pay ${plan?.price_display || "₹699"}`} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                Secured by Razorpay · UPI, cards, netbanking, wallets
              </p>
            </div>
          </section>
        )}

        {!loading && sub?.subscribed && q && (
          <>
            <section data-testid="usage-panel" className="mt-10 border-2 border-black bg-white">
              <div className="flex items-center justify-between p-6 border-b-2 border-black bg-black text-white">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/70">/ Current cycle</p>
                  <h2 className="font-heading font-black text-2xl mt-1">
                    {plan?.name || "Standard"} · {plan?.price_display || "₹699"}/mo
                  </h2>
                </div>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
                  <Clock className="w-3 h-3" />
                  {fmtDate(sub.cycle_start)} → {fmtDate(sub.cycle_end)}
                </span>
              </div>

              <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div data-testid="counter-included" className="border border-black/20 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Included</p>
                  <p className="mt-2 font-heading font-black text-3xl">
                    <span data-testid="counter-included-used">{q.included_used}</span>
                    <span className="text-zinc-400"> / {q.included_total}</span>
                  </p>
                  <div className="mt-3 h-2 bg-zinc-100 border border-black overflow-hidden">
                    <div
                      className="h-full bg-[#3B82F6] transition-all"
                      style={{ width: `${includedPct}%` }}
                    />
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {q.included_remaining} remaining
                  </p>
                </div>

                <div data-testid="counter-extra" className="border border-black/20 p-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Extra credits</p>
                  <p className="mt-2 font-heading font-black text-3xl">
                    {q.extra_remaining}
                    <span className="text-zinc-400 text-lg"> / +{q.extra_purchased}</span>
                  </p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    Bought {alreadyPurchased} · Cap {maxExtra}
                  </p>
                </div>

                <div data-testid="counter-total" className="border border-black/20 p-4 bg-[#DBEAFE]">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#1E40AF]">Total available</p>
                  <p className="mt-2 font-heading font-black text-3xl text-[#1E40AF]">
                    {q.total_available_now}
                  </p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#1E40AF]/80">
                    videos you can still make this cycle
                  </p>
                </div>
              </div>

              {q.low_warning && (
                <div data-testid="alert-low" className="mx-6 mb-6 border-2 border-amber-400 bg-amber-50 p-4 flex items-start gap-3">
                  <Zap className="w-4 h-4 text-amber-700 mt-0.5" />
                  <p className="text-sm text-amber-900">
                    You have only <b>{q.included_remaining} videos</b> remaining in your Standard Plan.
                    Consider buying extra credits below.
                  </p>
                </div>
              )}

              {q.exhausted && (
                <div data-testid="alert-exhausted" className="mx-6 mb-6 border-2 border-red-500 bg-red-50 p-4 flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-red-700 mt-0.5" />
                  <p className="text-sm text-red-900">
                    You&apos;ve used all videos in your Standard Plan for this cycle.
                    Purchase additional video credits to continue generating notes.
                  </p>
                </div>
              )}
            </section>

            <section data-testid="credit-packs" className="mt-10">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">/ Extra credits</p>
                  <h2 className="font-heading font-black text-2xl mt-1">Buy more videos</h2>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  ₹24 / video · max +{maxExtra} / cycle
                </span>
              </div>

              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {packs.map((p) => {
                  const disabled = busy || p.videos > canPurchaseMax;
                  return (
                    <button
                      key={p.videos}
                      data-testid={`pack-${p.videos}`}
                      disabled={disabled}
                      onClick={() => doBuy(p.videos)}
                      className={`text-left border-2 border-black p-4 bg-white hover:bg-[#DBEAFE] transition-colors disabled:opacity-40 disabled:cursor-not-allowed btn-brutal`}
                    >
                      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                        Extra videos
                      </p>
                      <p className="mt-1 font-heading font-black text-3xl flex items-center gap-1">
                        <Plus className="w-4 h-4" />
                        {p.videos}
                      </p>
                      <p className="mt-2 font-mono text-sm">{p.price_display}</p>
                    </button>
                  );
                })}
              </div>
              {canPurchaseMax <= 0 && (
                <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-red-600">
                  You&apos;ve reached the +{maxExtra} extra-credits cap for this cycle.
                </p>
              )}
            </section>
          </>
        )}

        <section className="mt-14 grid md:grid-cols-2 gap-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">/ Subscription history</p>
            <div className="mt-3 border border-black/20 bg-white divide-y divide-black/10">
              {(history.subscription_payments || []).length === 0 && (
                <p className="p-4 text-sm text-zinc-500">No payments yet.</p>
              )}
              {(history.subscription_payments || []).map((p) => (
                <div key={p.id} className="p-4 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">Standard plan</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {fmtDate(p.cycle_start)} → {fmtDate(p.cycle_end)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold">{fmtINR(p.amount_paise)}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {p.method} · {p.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">/ Credit purchase history</p>
            <div className="mt-3 border border-black/20 bg-white divide-y divide-black/10">
              {(history.credit_purchases || []).length === 0 && (
                <p className="p-4 text-sm text-zinc-500">No credit purchases yet.</p>
              )}
              {(history.credit_purchases || []).map((c) => (
                <div key={c.id} className="p-4 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">+{c.videos} videos</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {fmtDate(c.purchased_at)}
                    </p>
                  </div>
                  <p className="font-heading font-bold">{fmtINR(c.amount_paise)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <p className="mt-10 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Billing rules: additional credits are valid only within the current cycle · unused credits expire at cycle end · included 33 videos renew each cycle · maximum +{maxExtra} extra credits per cycle.
        </p>
      </main>
    </div>
  );
}
